'use client'

import { Icon } from '@components/primitives/Icon'
import type { ReducedConjuredArticleElement } from '.'
import { TableOfContentsHook } from './TableOfContentsHook'
import { Text } from '@components/primitives/Text'
import { classMerge } from '@components/utilities/classMerge'
import { faBook } from '@fortawesome/pro-solid-svg-icons/faBook'
import kebabCase from 'lodash/kebabCase'
import { useEffect } from 'react'

const HorizontalTableOfContentsId = 'HorezontalTableOfContents'

export default function TableOfContents({
  elements,
}: {
  elements: ReducedConjuredArticleElement[]
}) {
  const uniqueElements = elements
    .sort((a, b) => a.priority - b.priority)
    .reduce((acc: ReducedConjuredArticleElement[], current) => {
      const x = acc.find((item) => item.briefHeading === current.briefHeading)
      if (!x) {
        return acc.concat([current as ReducedConjuredArticleElement])
      } else {
        return acc
      }
    }, [])

  const { activeId } = TableOfContentsHook({ elements: uniqueElements })

  useEffect(() => {
    const parent = document.getElementById(HorizontalTableOfContentsId)
    const element = document.getElementById(`step-${activeId}`)

    // scrollToView doesn't work well for horizontal scrolling
    parent?.scrollTo({
      behavior: 'smooth',
      left: (element?.offsetLeft ?? 0) - (parent?.offsetLeft ?? 0),
    })
  }, [activeId])

  return (
    <>
      <div className='absolute left-0 top-20 hidden h-full pb-10 pl-16 pt-12 2xl:block'>
        <div className='relative h-full pb-10 pl-4'>
          <div className='sticky top-20 w-full text-wrap '>
            <div className='-ml-3 flex items-center space-x-2 '>
              <Icon
                className='mb-1.5 rounded-md border border-neutral200 p-1.5 text-neutral600 shadow '
                icon={faBook}
              />
              <Text className='mb-2 bg-white text-sm font-bold text-neutral900'>
                Table of Contents
              </Text>
            </div>
            {uniqueElements.map((heading, index) => (
              <div
                className={classMerge(
                  'border-l-2 py-2 pl-8 transition-all duration-150 ease-in-out',
                  activeId === kebabCase(heading.heading)
                    ? ' border-primary400 '
                    : ' border-neutral200 hover:border-primary400',
                )}
                key={index}
              >
                <Text
                  className={
                    activeId === kebabCase(heading.heading)
                      ? 'font-semibold text-primary400'
                      : 'text-neutral500'
                  }
                  onClick={() => handleVerticalTableOfContentsClick(heading)}
                  styleName='p-xtiny'
                >
                  {heading.briefHeading}
                </Text>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className='sticky top-[4.3em] z-10 border-neutral200 bg-white pt-3 2xl:hidden'>
        <div
          className='hide-scrollbars  inline-flex w-full overflow-x-scroll'
          id={HorizontalTableOfContentsId}
        >
          {uniqueElements.map((heading, index) => (
            <p
              className={classMerge(
                'shrink-0 border-y border-t-neutral200 px-4 py-3 active:border-b-black active:text-black',
                activeId === kebabCase(heading.heading)
                  ? 'border-b-2 border-b-black'
                  : 'border-b-neutral200 text-neutral400 hover:border-b-2 hover:border-b-neutral400',
              )}
              id={`step-${kebabCase(heading.heading)}`}
              key={index}
              onClick={() => {
                handleHorizontalTableOfContentsClick(heading)
              }}
            >
              {heading.briefHeading}
            </p>
          ))}
        </div>
      </div>
    </>
  )
}

function handleVerticalTableOfContentsClick(
  heading: ReducedConjuredArticleElement,
) {
  const element = document.getElementById(`${kebabCase(heading.heading)}`)

  if (element) {
    element.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    })
  }
}

function handleHorizontalTableOfContentsClick(
  heading: ReducedConjuredArticleElement,
) {
  const element = document.getElementById(`${kebabCase(heading.heading)}`)

  if (element) {
    element.scrollIntoView({
      behavior: 'instant',
      block: 'center',
      inline: 'center',
    })
  }
}
